import styled, { css, keyframes } from 'styled-components'
import theme from '@app/ui/theme'

type Tone = 'neutral' | 'active' | 'success' | 'danger' | 'locked'

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const toneStyles = {
  neutral: css`
    color: ${theme.colors.secondary};
    background: ${theme.colors.light2};
    border-color: ${theme.colors.light};
  `,
  active: css`
    color: ${theme.colors.secondary2};
    background: #edf4fd;
    border-color: #cadcf0;
  `,
  success: css`
    color: ${theme.colors.accept};
    background: #ebf7f0;
    border-color: #c5dfd2;
  `,
  danger: css`
    color: ${theme.colors.warning};
    background: #fff2f2;
    border-color: #f0cccc;
  `,
  locked: css`
    color: ${theme.colors.color10};
    background: ${theme.colors.light3};
    border-color: ${theme.colors.light4};
  `,
}

export const Header = styled.div`
  padding: 28px 28px 20px;
  background: ${theme.colors.light2};
  border-bottom: 1px solid ${theme.colors.light};
  color: ${theme.colors.primary};
`

export const Title = styled.h2`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: 0.01em;
`

export const Subtitle = styled.p`
  margin: 10px 0 0;
  font-size: 15px;
  line-height: 1.5;
  color: ${theme.colors.secondary};
`

export const TeeRoute = styled.div`
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`

export const TeePill = styled.div`
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  color: ${theme.colors.primary};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.light};
`

export const RouteArrow = styled.span`
  font-size: 15px;
  color: ${theme.colors.secondary};
`

export const Body = styled.div`
  padding: 20px 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

export const SummaryCard = styled.div`
  border-radius: 12px;
  border: 1px solid ${theme.colors.light};
  background: ${theme.colors.light3};
  padding: 10px 12px;
`

export const SummaryLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
`

export const SummaryValue = styled.div`
  margin-top: 6px;
  font-size: 22px;
  font-weight: 800;
  color: ${theme.colors.primary};
`

export const ProgressMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

export const ProgressMeta = styled.span`
  font-size: 14px;
  color: ${theme.colors.secondary};
  font-weight: 600;
`

export const ProgressTrack = styled.div`
  width: 100%;
  height: 12px;
  border-radius: 999px;
  background: ${theme.colors.light2};
  overflow: hidden;
`

export const ProgressFill = styled.div<{ $width: number }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  border-radius: 999px;
  background: ${theme.colors.secondary2};
  transition: width 250ms ease;
`

export const CurrentStateRow = styled.div`
  min-height: 38px;
  display: flex;
  align-items: center;
`

export const ErrorBanner = styled.div`
  border-radius: 12px;
  border: 1px solid #f0cccc;
  background: #fff2f2;
  color: ${theme.colors.warning};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  padding: 10px 12px;
`

export const ListTitle = styled.h3`
  margin: 4px 0 0;
  font-size: 16px;
  color: ${theme.colors.primary};
`

export const HoleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const HoleRow = styled.div`
  border: 1px solid ${theme.colors.light};
  background: ${theme.colors.white};
  border-radius: 12px;
  padding: 12px 14px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
`

export const HoleTitle = styled.div`
  color: ${theme.colors.primary};
  font-size: 15px;
  font-weight: 700;
`

export const HoleMeta = styled.div`
  margin-top: 2px;
  color: ${theme.colors.secondary};
  font-size: 13px;
`

export const StatusPill = styled.div<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 999px;
  border: 1px solid;
  font-size: 12px;
  font-weight: 700;
  ${({ $tone }) => toneStyles[$tone]};
`

export const StatusIcon = styled.span<{ $spin?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${({ $spin }) =>
    $spin &&
    css`
      animation: ${spin} 1s linear infinite;
    `};
`

export const FooterMessage = styled.span`
  font-size: 14px;
  color: ${theme.colors.secondary};
  font-weight: 600;
`

export const FooterAction = styled.button`
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  min-width: 110px;
  background: ${theme.colors.secondary2};
  color: ${theme.colors.white};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`
